import React, { useState, useEffect, useCallback } from 'react';
import { useLinks } from '../../hooks/useLinks';
import { useDomains } from '../../hooks/useDomains';
import urlMetadataService from '../../services/urlMetadataService';
import { getDefaultDomain } from '../../utils/domainUtils';

const ImprovedCreateLinkModal = ({ isOpen, onClose, onSuccess }) => {
  const { createLink, loading, error, clearError } = useLinks();
  const { domains } = useDomains();

  const [formData, setFormData] = useState({
    originalUrl: '',
    domainId: '',
    customSlug: '',
    title: '',
    description: '',
    tags: '',
    expiresAt: '',
    password: '',
    imageUrl: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [metadataFetched, setMetadataFetched] = useState(false);
  const [linkPreview, setLinkPreview] = useState({
    url: '',
    title: '',
    description: '',
    image: ''
  });

  // Auto-generate slug from title or URL (max 20 characters)
  const generateSlug = useCallback((title, url) => {
    let slug = '';

    if (title && title.length > 0) {
      slug = title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/^-+|-+$/g, '') // Remove leading/trailing dashes
        .substring(0, 20);
    }

    if (!slug && url) {
      try {
        const urlObj = new URL(url);
        const path = urlObj.pathname.split('/').filter(Boolean).pop();
        if (path) {
          slug = path
            .replace(/[^a-z0-9-]/gi, '')
            .toLowerCase()
            .substring(0, 20);
        }
      } catch {}
    }

    // Fallback: generate random slug (6 characters)
    if (!slug) {
      slug = Math.random().toString(36).substring(2, 8);
    }

    // Ensure minimum length and max 20 characters
    return slug.length >= 3 ? slug : slug + Math.random().toString(36).substring(2, 5);
  }, []);

  // Update link preview when form changes
  useEffect(() => {
    const selectedDomain = domains.find(d => d.id === formData.domainId);
    const domainName = selectedDomain ? selectedDomain.domain : getDefaultDomain(domains);
    const slug = formData.customSlug || 'abc123';

    setLinkPreview(prev => ({
      ...prev,
      url: `https://${domainName}/${slug}`
    }));
  }, [formData.domainId, formData.customSlug, domains]);

  // Fetch metadata when URL changes
  const fetchUrlMetadata = useCallback(async (url) => {
    try {
      setIsLoadingMetadata(true);
      console.log('🔍 Fetching metadata for:', url);

      const metadata = await urlMetadataService.fetchMetadata(url);

      if (metadata.success) {
        // Generate slug from title if not already set
        const autoSlug = !formData.customSlug ? generateSlug(metadata.title, url) : formData.customSlug;

        setFormData(prev => ({
          ...prev,
          title: prev.title || metadata.title || '',
          description: prev.description || metadata.description || '',
          customSlug: prev.customSlug || autoSlug
        }));

        setLinkPreview(prev => ({
          ...prev,
          title: metadata.title || '',
          description: metadata.description || '',
          image: metadata.image || ''
        }));

        // Store image URL in form data for saving
        setFormData(prev => ({
          ...prev,
          imageUrl: metadata.image || ''
        }));

        setMetadataFetched(true);
        console.log('✅ Metadata applied:', metadata);
      }

    } catch (error) {
      console.error('❌ Error fetching metadata:', error);
    } finally {
      setIsLoadingMetadata(false);
    }
  }, [formData.customSlug, generateSlug]);

  // Debounced metadata fetch
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (formData.originalUrl &&
          formData.originalUrl.match(/^https?:\/\//) &&
          !metadataFetched) {
        await fetchUrlMetadata(formData.originalUrl);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [formData.originalUrl, metadataFetched, fetchUrlMetadata]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear errors
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (error) clearError();

    // Reset metadata when URL changes
    if (name === 'originalUrl') {
      setMetadataFetched(false);
      setLinkPreview(prev => ({ ...prev, title: '', description: '', image: '' }));
    }
  };

  const handleOptimizeSlug = () => {
    const optimizedSlug = generateSlug(formData.title || linkPreview.title, formData.originalUrl);
    setFormData(prev => ({ ...prev, customSlug: optimizedSlug }));
  };

  const generateUniqueSlug = (baseSlug) => {
    // Add random suffix to make slug unique
    const randomSuffix = Math.random().toString(36).substring(2, 5);
    return `${baseSlug}-${randomSuffix}`;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.originalUrl.trim()) {
      errors.originalUrl = 'URL is required';
    } else {
      try {
        new URL(formData.originalUrl);
      } catch {
        errors.originalUrl = 'Please enter a valid URL';
      }
    }

    if (formData.customSlug) {
      if (formData.customSlug.length < 3) {
        errors.customSlug = 'Slug must be at least 3 characters';
      } else if (!/^[a-zA-Z0-9-_]+$/.test(formData.customSlug)) {
        errors.customSlug = 'Slug can only contain letters, numbers, hyphens, and underscores';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const linkData = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean) : [],
        domainId: formData.domainId || null,
        expiresAt: formData.expiresAt || null,
        password: formData.password || null
      };

      console.log('🔄 CreateModal: Submitting link data:', linkData);
      console.log('🖼️ CreateModal: Image URL being sent:', linkData.imageUrl);
      console.log('📝 CreateModal: Description being sent:', linkData.description);

      let newLink;
      try {
        newLink = await createLink(linkData);
        console.log('✅ CreateModal: Link created successfully:', newLink);
      } catch (createError) {
        // Handle duplicate slug error
        if (createError.message.includes('already taken')) {
          console.log('🔄 CreateModal: Slug taken, trying with unique suffix...');

          // Generate unique slug and retry
          const uniqueSlug = generateUniqueSlug(linkData.customSlug || 'link');
          const retryLinkData = { ...linkData, customSlug: uniqueSlug };

          console.log('🔄 CreateModal: Retrying with slug:', uniqueSlug);
          newLink = await createLink(retryLinkData);
          console.log('✅ CreateModal: Link created with unique slug:', newLink);

          // Update form to show the final slug used
          setFormData(prev => ({ ...prev, customSlug: uniqueSlug }));
        } else {
          throw createError;
        }
      }

      // Reset and close
      handleClose();
      if (onSuccess) onSuccess(newLink);

    } catch (err) {
      console.error('❌ CreateModal: Failed to create link:', err);
    }
  };

  const handleClose = () => {
    setFormData({
      originalUrl: '', domainId: '', customSlug: '', title: '',
      description: '', tags: '', expiresAt: '', password: '', imageUrl: ''
    });
    setFormErrors({});
    setMetadataFetched(false);
    setIsLoadingMetadata(false);
    setLinkPreview({ url: '', title: '', description: '', image: '' });
    clearError();
    onClose();
  };

  const verifiedDomains = domains.filter(domain => domain.isVerified);
  const defaultDomain = getDefaultDomain(domains);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content improved-create-modal">
        <div className="modal-header">
          <h2>🔗 Create Short Link</h2>
          <button className="modal-close" onClick={handleClose} disabled={loading}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="create-form">
            <div className="form-section">
              <div className="form-left">
                {/* Destination URL */}
                <div className="form-group">
                  <label className="form-label">
                    Destination URL
                    {isLoadingMetadata && (
                      <span className="loading-indicator">
                        <span className="spinner-small"></span>
                        Fetching page info...
                      </span>
                    )}
                  </label>
                  <textarea
                    name="originalUrl"
                    value={formData.originalUrl}
                    onChange={handleInputChange}
                    className={`url-textarea ${formErrors.originalUrl ? 'error' : ''} ${isLoadingMetadata ? 'loading' : ''}`}
                    placeholder="Type or paste a link (URL)"
                    disabled={loading}
                    rows="3"
                  />
                  {formErrors.originalUrl && (
                    <span className="field-error">{formErrors.originalUrl}</span>
                  )}
                </div>

                {/* Domain and Slug */}
                <div className="domain-slug-section">
                  <div className="form-group">
                    <label className="form-label">Branded domain</label>
                    <select
                      name="domainId"
                      value={formData.domainId}
                      onChange={handleInputChange}
                      className="domain-select"
                      disabled={loading}
                    >
                      <option value="">{defaultDomain} (default)</option>
                      {verifiedDomains.map(domain => (
                        <option key={domain.id} value={domain.id}>
                          {domain.domain}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Back-half</label>
                    <div className="slug-input-group">
                      <input
                        name="customSlug"
                        value={formData.customSlug}
                        onChange={handleInputChange}
                        className={`slug-input ${formErrors.customSlug ? 'error' : ''}`}
                        placeholder="signature-slug (eg. card)"
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="optimize-button"
                        onClick={handleOptimizeSlug}
                        disabled={loading || !formData.originalUrl}
                        title="Generate optimized slug"
                      >
                        Optimize
                      </button>
                    </div>
                    {formErrors.customSlug && (
                      <span className="field-error">{formErrors.customSlug}</span>
                    )}
                    <span className="field-hint">
                      Suggested with dash (SEO friendly) back-half
                    </span>
                  </div>
                </div>

                {/* Advanced Options */}
                <div className="advanced-section">
                  <div className="form-group">
                    <label className="form-label">Title (Optional)</label>
                    <input
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Custom title for this link"
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Description (Optional)</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Brief description"
                      rows="2"
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Tags (Optional)</label>
                    <input
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="marketing, campaign, social"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Options */}
                <div className="options-section">
                  <div className="toggle-option">
                    <label className="toggle-label">
                      <input type="checkbox" className="toggle-input" />
                      <span className="toggle-slider"></span>
                      Copy to clipboard
                    </label>
                  </div>

                  <div className="toggle-option">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        className="toggle-input"
                        checked={!!formData.password}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({ ...prev, password: 'password123' }));
                          } else {
                            setFormData(prev => ({ ...prev, password: '' }));
                          }
                        }}
                      />
                      <span className="toggle-slider"></span>
                      Password protect this link
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-right">
                <div className="preview-section">
                  <div className="preview-header">
                    <h3>Link preview</h3>
                    <button type="button" className="edit-preview-btn" title="Edit preview">
                      ✏️
                    </button>
                  </div>

                  <div className="link-preview-card">
                    {linkPreview.image ? (
                      <img src={linkPreview.image} alt="Link preview" className="preview-image" />
                    ) : (
                      <div className="preview-image-placeholder">
                        <span className="image-icon">🔗</span>
                      </div>
                    )}

                    <div className="preview-content">
                      <div className="preview-url">{linkPreview.url}</div>
                      <div className="preview-title">
                        {linkPreview.title || formData.title || 'No title'}
                      </div>
                      <div className="preview-description">
                        {linkPreview.description || formData.description || 'No description'}
                      </div>
                    </div>
                  </div>

                  {metadataFetched && (
                    <div className="metadata-status">
                      ✅ Page info loaded automatically
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || !formData.originalUrl}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Creating...
              </>
            ) : (
              'Create link'
            )}
          </button>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .improved-create-modal {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          max-width: 900px;
          max-height: 90vh;
          width: 90vw;
          overflow: hidden;
          position: relative;
        }

        .modal-header {
          padding: 25px 30px 20px;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #f8f9fa;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 20px;
          color: #333;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          padding: 8px;
          border-radius: 50%;
          color: #6c757d;
          transition: all 0.2s;
        }

        .modal-close:hover {
          background: #e9ecef;
          color: #333;
        }

        .modal-body {
          padding: 30px;
          max-height: calc(90vh - 140px);
          overflow-y: auto;
        }

        .modal-footer {
          padding: 20px 30px;
          border-top: 1px solid #e9ecef;
          display: flex;
          justify-content: flex-end;
          gap: 15px;
          background: #f8f9fa;
        }

        .create-form {
          padding: 0;
        }

        .form-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
          min-height: 400px;
        }

        .form-left {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-right {
          border-left: 1px solid #e9ecef;
          padding-left: 30px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-weight: 600;
          color: #333;
          font-size: 14px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .loading-indicator {
          font-size: 12px;
          color: #007bff;
          display: flex;
          align-items: center;
          gap: 6px;
          font-weight: normal;
        }

        .spinner-small {
          width: 12px;
          height: 12px;
          border: 2px solid #f3f4f6;
          border-top: 2px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .url-textarea {
          min-height: 80px;
          padding: 12px;
          border: 1px solid #ced4da;
          border-radius: 8px;
          font-size: 14px;
          resize: vertical;
          font-family: inherit;
        }

        .url-textarea.loading {
          border-color: #007bff;
          background: #f8f9ff;
        }

        .url-textarea:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0,123,255,.25);
        }

        .domain-slug-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
        }

        .domain-select, .form-input {
          padding: 10px 12px;
          border: 1px solid #ced4da;
          border-radius: 6px;
          font-size: 14px;
        }

        .slug-input-group {
          display: flex;
          gap: 8px;
        }

        .slug-input {
          flex: 1;
          padding: 10px 12px;
          border: 1px solid #ced4da;
          border-radius: 6px;
          font-size: 14px;
        }

        .optimize-button {
          background: #333;
          color: white;
          border: none;
          padding: 10px 16px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .optimize-button:hover:not(:disabled) {
          background: #555;
        }

        .optimize-button:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .field-hint {
          font-size: 12px;
          color: #6c757d;
          margin-top: 4px;
        }

        .advanced-section {
          display: flex;
          flex-direction: column;
          gap: 15px;
          padding-top: 10px;
        }

        .options-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding-top: 15px;
          border-top: 1px solid #e9ecef;
        }

        .toggle-option {
          display: flex;
          align-items: center;
        }

        .toggle-label {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          font-size: 14px;
          color: #333;
        }

        .toggle-input {
          display: none;
        }

        .toggle-slider {
          width: 44px;
          height: 24px;
          background: #ccc;
          border-radius: 12px;
          position: relative;
          transition: background 0.3s;
        }

        .toggle-slider::before {
          content: '';
          position: absolute;
          width: 20px;
          height: 20px;
          background: white;
          border-radius: 50%;
          top: 2px;
          left: 2px;
          transition: transform 0.3s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }

        .toggle-input:checked + .toggle-slider {
          background: #007bff;
        }

        .toggle-input:checked + .toggle-slider::before {
          transform: translateX(20px);
        }

        .preview-section {
          height: 100%;
        }

        .preview-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 15px;
        }

        .preview-header h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 0;
          color: #333;
        }

        .edit-preview-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          font-size: 14px;
        }

        .edit-preview-btn:hover {
          background: #f8f9fa;
        }

        .link-preview-card {
          border: 1px solid #e9ecef;
          border-radius: 12px;
          overflow: hidden;
          background: white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .preview-image {
          width: 100%;
          height: 160px;
          object-fit: cover;
        }

        .preview-image-placeholder {
          width: 100%;
          height: 160px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c757d;
        }

        .image-icon {
          font-size: 48px;
        }

        .preview-content {
          padding: 20px;
        }

        .preview-url {
          font-size: 12px;
          color: #007bff;
          margin-bottom: 8px;
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .preview-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin-bottom: 8px;
          line-height: 1.3;
        }

        .preview-description {
          font-size: 14px;
          color: #6c757d;
          line-height: 1.4;
        }

        .metadata-status {
          margin-top: 10px;
          font-size: 12px;
          color: #28a745;
          text-align: center;
        }

        .field-error {
          color: #dc3545;
          font-size: 12px;
          margin-top: 4px;
        }

        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .btn {
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #0056b3;
          transform: translateY(-1px);
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #545b62;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @media (max-width: 768px) {
          .modal-overlay {
            padding: 10px;
          }

          .improved-create-modal {
            max-width: none;
            width: 100%;
            max-height: 95vh;
          }

          .modal-header,
          .modal-body,
          .modal-footer {
            padding-left: 20px;
            padding-right: 20px;
          }

          .form-section {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .form-right {
            border-left: none;
            border-top: 1px solid #e9ecef;
            padding-left: 0;
            padding-top: 20px;
          }

          .domain-slug-section {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default ImprovedCreateLinkModal;